// Versions: subir la web en zip, publicarla, dejarla en vista previa y volver a una versión anterior.
import { useCallback, useEffect, useRef, useState } from 'react';
import { MoreHorizontal, Upload } from 'lucide-react';
import { api, type Release } from '../../api.ts';
import { t } from '../../i18n/index.ts';
import { errorText, useApp } from '../../store.ts';
import { ago, bytes, fullDate } from '../../format.ts';
import { MenuButton } from '../../ui/Menu.tsx';
import type { SiteProps } from './SiteShell.tsx';

export function Versions({ site, onChange }: SiteProps) {
  const notify = useApp((s) => s.notify);
  const config = useApp((s) => s.config);
  const [releases, setReleases] = useState<Release[] | null>(null);
  const [publishNow, setPublishNow] = useState(!site.published);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setReleases((await api.releases(site.id)).releases);
  }, [site.id]);

  useEffect(() => {
    load().catch((e) => notify(errorText(e), true));
  }, [load, notify]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      notify(t('versions.onlyZip'), true);
      return;
    }
    if (config && file.size > config.maxUploadMb * 1024 * 1024) {
      notify(t('versions.tooBig', { mb: config.maxUploadMb }), true);
      return;
    }
    setBusy(true);
    try {
      const r = await api.upload(site.id, file, publishNow);
      onChange(r.site);
      await load();
      notify(publishNow ? t('versions.publishedToast', { n: r.release.number }) : t('versions.uploadedToast', { n: r.release.number }));
    } catch (e) {
      notify(errorText(e), true);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      await load();
      notify(ok);
    } catch (e) {
      notify(errorText(e), true);
    }
  };

  return (
    <>
      <section className="section">
        <h2>{t('site.versions')}</h2>
        <p className="hint">{t('versions.hint')}</p>
        <div
          className={`dropzone${over ? ' over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void upload(e.dataTransfer.files[0]);
          }}
        >
          <Upload size={20} color="var(--icon)" />
          <span>{busy ? t('versions.uploading') : t('versions.drop')}</span>
          <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
            <button type="button" className="btn primary" disabled={busy || site.archived} onClick={() => input.current?.click()}>
              {t('versions.choose')}
            </button>
            <label className="check">
              <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} />
              {t('versions.publishNow')}
            </label>
          </div>
          <input ref={input} type="file" accept=".zip,application/zip" hidden onChange={(e) => void upload(e.target.files?.[0])} />
        </div>
      </section>

      <section className="section">
        <h3>{t('versions.history')}</h3>
        {releases && releases.length === 0 ? <p className="hint">{t('versions.none')}</p> : null}
        <div className="list">
          {(releases ?? []).map((r) => (
            <div key={r.id} className="list-row">
              <div className="col grow" style={{ gap: 4 }}>
                <div className="row">
                  <span>{t('versions.number', { n: r.number })}</span>
                  {r.live ? <span className="badge blue">{t('projects.live')}</span> : null}
                  {r.staging ? <span className="badge">{t('versions.preview')}</span> : null}
                </div>
                <span className="hint ellipsis" title={fullDate(r.createdAt)}>
                  {t('versions.meta', { when: ago(r.createdAt), files: r.fileCount, size: bytes(r.sizeBytes) })}
                </span>
              </div>
              {!r.live ? (
                <button type="button" className="btn small" disabled={site.archived} onClick={() => act(async () => onChange((await api.publishRelease(r.id)).site), t('versions.publishedToast', { n: r.number }))}>
                  {t('site.publish')}
                </button>
              ) : null}
              <MenuButton
                align="end"
                title={t('common.more')}
                items={[
                  { label: t('versions.setPreview'), disabled: r.staging, onSelect: () => act(async () => onChange((await api.stageRelease(r.id)).site), t('versions.previewSet')) },
                  { label: t('versions.openPreview'), disabled: !r.staging, onSelect: () => window.open(site.hosts.previewUrl, '_blank', 'noopener') },
                  { separator: true },
                  { label: t('common.delete'), danger: true, disabled: r.live || r.staging, onSelect: () => act(() => api.deleteRelease(r.id), t('versions.deleted')) },
                ]}
              >
                <MoreHorizontal size={14} />
              </MenuButton>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
